import {
    ActionReducerMapBuilder,
    Draft,
    SerializedError,
    createAsyncThunk,
    createSlice,
} from '@reduxjs/toolkit';
import { IAction, IAppReduxStore, SliceProps } from '../store';
import { urlParamReplicaId, urlProjectUserEmailList, urlProjectUserlist } from '../../Constants';
import { apiFetch } from '../../util/ProjectUtils';

/**
 * Initial slice state value.
 */
const initialState: IProjectUserListState = {
    loading: false,
    error: null,
    users: [],
    groups: [],
    userEmails: [],
};

const reducers = {
    clearProjectUserList(state: IProjectUserListState) {
        state.users = [];
    },
    resetGetProjectUsersErrorState(state: IProjectUserListState) {
        state.error = null;
    },
};
/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Get users list
     */
    getUserList: async (config: { replicaId: string }): Promise<IGetUserListResponse> => {
        try {
            const { replicaId } = config;
            const url = urlProjectUserlist.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Get users email list
     */
    getUserEmailList: async (config: { replicaId: string }): Promise<IGetUserEmailListResponse> => {
        try {
            const { replicaId } = config;
            const url = urlProjectUserEmailList.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectUserListState>) => {
        /**
         * Reducers for the 'getUserList' thunk.
         */
        builder.addCase(getUserList.pending, (state: Draft<IProjectUserListState>) => {
            state.loading = true;
            state.error = null;
        });

        builder.addCase(
            getUserList.fulfilled,
            (state: Draft<IProjectUserListState>, action: IAction<IGetUserListResponse>) => {
                state.loading = false;
                state.error = null;
                state.users = action.payload.userList;
                state.groups = action.payload.groups;
            },
        );

        builder.addCase(
            getUserList.rejected,
            (state: Draft<IProjectUserListState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
            },
        );

        /**
         * Reducers for the 'getUserEmailList' thunk.
         */
        builder.addCase(getUserEmailList.pending, (state: Draft<IProjectUserListState>) => {
            state.loading = true;
            state.error = null;
        });

        builder.addCase(
            getUserEmailList.fulfilled,
            (state: Draft<IProjectUserListState>, action: IAction<IGetUserEmailListResponse>) => {
                state.loading = false;
                state.error = null;

                // Filter exact duplicates of contacts
                const contacts: IProjectUserEmailContact[] = [];
                action.payload.userList.forEach(contact => {
                    if (!contacts.some(c => JSON.stringify(c) === JSON.stringify(contact))) {
                        contacts.push(contact);
                    }
                });

                state.userEmails = contacts;
            },
        );

        builder.addCase(
            getUserEmailList.rejected,
            (state: Draft<IProjectUserListState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
            },
        );
    },
};

export const getUserList = createAsyncThunk(`${urlProjectUserlist}`, thunks.getUserList);
export const getUserEmailList = createAsyncThunk(
    `${urlProjectUserEmailList}`,
    thunks.getUserEmailList,
);

/**
 * Interface that describes the state object for this redux slice.
 */
export interface IProjectUserListState extends SliceProps {
    users: IProjectUserContact[];
    groups: IDistributionGroup[];
    userEmails: IProjectUserEmailContact[];
}

interface IGetUserListResponse {
    userList: IProjectUserContact[];
    groups: IDistributionGroup[];
}

interface IGetUserEmailListResponse {
    userList: IProjectUserEmailContact[];
}

export interface IProjectUserContact {
    name: string;
    notesName: string;
    email: string;
    company?: string;
}

export interface IProjectUserEmailContact {
    name: string;
    email: string[];
    company?: string;
}

export interface IDistributionGroup {
    name: string;
    userList: IProjectUserContact[];
}

export function isDistributionGroup(object: unknown): object is IDistributionGroup {
    return object && typeof object === 'object' && 'name' in object && 'userList' in object;
}

/**
 * Create the redux slice.
 */
export const projectUserListSlice = createSlice({
    name: 'projectUserList',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

export function getProjectUserListWithoutCurrentUser(store: IAppReduxStore): IProjectUserContact[] {
    return store.projectUserList.users.filter(user => user.email !== store.userProfile.data.email);
}

/**
 * Export functions from slice actions.
 */
export const { clearProjectUserList, resetGetProjectUsersErrorState } =
    projectUserListSlice.actions;

/**
 * Export slice reducer as default.
 */
export default projectUserListSlice.reducer;
